import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Package, Layers, Globe, Target, Info, Barcode, ChevronRight, ChevronDown,
  Search, Check,
} from 'lucide-react';
import { AudienceScope, DripCampaign } from '@/actions/drip-campaigns';

export interface TreeVariant {
  variant_id: number;
  sku: string | null;
  name: string | null;
  price: number | null;
}
export interface TreeProduct { product_id: number; name: string; variants?: TreeVariant[] }
export interface TreeCat { cat_id: number; cat: string; colour: string; products: TreeProduct[] }

export const AUDIENCE_SCOPES: {
  value: AudienceScope; label: string; hint: string; icon: React.ReactNode;
}[] = [
  {
    value: 'variants', label: 'Specific SKUs (pack sizes)',
    hint: 'Pick individual packs. A 30-day pack and a 90-day pack of the same product can run different journeys.',
    icon: <Barcode className="w-4 h-4" />,
  },
  {
    value: 'product', label: 'One product',
    hint: 'Only orders containing a single chosen product — every pack size of it.',
    icon: <Target className="w-4 h-4" />,
  },
  {
    value: 'products', label: 'Specific products',
    hint: 'Pick the products this journey should cover, all pack sizes included.',
    icon: <Package className="w-4 h-4" />,
  },
  {
    value: 'category', label: 'A whole category',
    hint: 'Every product in the chosen categories — the same categories used for Round Robin.',
    icon: <Layers className="w-4 h-4" />,
  },
  {
    value: 'all', label: 'Every product',
    hint: 'A catch-all. Only used when no more specific journey covers the order.',
    icon: <Globe className="w-4 h-4" />,
  },
];

/** Every variant in the tree, flattened, with the product it belongs to. */
export const allVariants = (
  tree: TreeCat[],
): Array<TreeVariant & { product_id: number; product_name: string }> =>
  tree.flatMap(c => c.products.flatMap(p =>
    (p.variants || []).map(v => ({ ...v, product_id: p.product_id, product_name: p.name }))
  ));

/**
 * Which products a scope + selection actually resolves to.
 *
 * Shared with the journeys list so the summary on a row and the count in the picker can
 * never disagree — they are the same function.
 *
 * A SKU selection resolves to the products those SKUs belong to. That is what the
 * "products with no journey" hint on the list counts, and a product with one pack size
 * covered is genuinely no longer uncovered.
 */
export const resolveCoveredProductIds = (
  scope: AudienceScope | undefined,
  tree: TreeCat[],
  opts: {
    productId?: number | null;
    productIds?: Iterable<number>;
    categoryIds?: Iterable<number>;
    variantIds?: Iterable<number>;
  },
): number[] => {
  const all = tree.flatMap(c => c.products.map(p => p.product_id));
  switch (scope) {
    case 'all':
      return all;
    case 'variants': {
      const chosen = new Set(Array.from(opts.variantIds || []));
      const products = new Set(
        allVariants(tree).filter(v => chosen.has(v.variant_id)).map(v => v.product_id)
      );
      return Array.from(products);
    }
    case 'products':
      return Array.from(opts.productIds || []);
    case 'category': {
      const cats = new Set(Array.from(opts.categoryIds || []));
      return tree.filter(c => cats.has(c.cat_id)).flatMap(c => c.products.map(p => p.product_id));
    }
    case 'product':
    default:
      return opts.productId ? [opts.productId] : [];
  }
};

/** Products a saved journey covers, from the audience rows the list endpoint returns. */
export const coveredProductIdsForJourney = (j: DripCampaign, tree: TreeCat[]): number[] =>
  resolveCoveredProductIds(j.audience_scope || 'product', tree, {
    productId: j.product_id_fk,
    productIds: (j.audience_targets || [])
      .filter(t => t.product_id_fk).map(t => t.product_id_fk as number),
    categoryIds: (j.audience_targets || [])
      .filter(t => t.category_id_fk).map(t => t.category_id_fk as number),
    variantIds: (j.audience_targets || [])
      .filter(t => t.variant_id_fk).map(t => t.variant_id_fk as number),
  });

/** Short human summary of a journey's audience, for a list row. */
export const audienceSummary = (j: DripCampaign, tree: TreeCat[]): string => {
  const scope = j.audience_scope || 'product';
  const n = coveredProductIdsForJourney(j, tree).length;
  if (scope === 'all') return `Every product (${n})`;
  if (scope === 'variants') {
    const targets = (j.audience_targets || []).filter(t => t.variant_id_fk);
    // One SKU is worth naming outright — "Ashwagandha Probiotic · 180 Capsules" says
    // far more on a list row than "1 SKU" does.
    if (targets.length === 1) {
      const t = targets[0];
      const pack = t.variant_title || t.variant_sku || 'SKU';
      return t.variant_product_title ? `${t.variant_product_title} · ${pack}` : pack;
    }
    return `${targets.length} SKUs · ${n} product${n === 1 ? '' : 's'}`;
  }
  if (scope === 'products') return `${(j.audience_targets || []).length} products`;
  if (scope === 'category') {
    const names = (j.audience_targets || [])
      .map(t => t.category_name).filter(Boolean) as string[];
    const label = names.length === 1 ? names[0] : `${names.length} categories`;
    return `${label} · ${n} products`;
  }
  const own = tree.flatMap(c => c.products).find(p => p.product_id === j.product_id_fk);
  return own?.name || 'One product';
};

/** A variant's display label: "180 Capsules · HAW180". */
const variantLabel = (v: TreeVariant): string =>
  [v.name, v.sku].filter(Boolean).join(' · ') || `Variant ${v.variant_id}`;

interface Props {
  tree: TreeCat[];
  scope: AudienceScope;
  onScopeChange: (s: AudienceScope) => void;
  /** 'product' scope — the single product */
  productId: number | null;
  onProductIdChange: (id: number | null) => void;
  /** 'products' scope */
  productIds: Set<number>;
  onProductIdsChange: (s: Set<number>) => void;
  /** 'category' scope */
  categoryIds: Set<number>;
  onCategoryIdsChange: (s: Set<number>) => void;
  /** 'variants' scope — chosen SKUs / pack sizes */
  variantIds: Set<number>;
  onVariantIdsChange: (s: Set<number>) => void;
}

/** Product row with its pack sizes, collapsed until opened. */
const VariantProductRow: React.FC<{
  product: TreeProduct;
  selected: Set<number>;
  onToggle: (variantId: number) => void;
  onToggleAll: (variantIds: number[], select: boolean) => void;
  /** Open on mount — used while a search is filtering the list. */
  forceOpen: boolean;
}> = ({ product, selected, onToggle, onToggleAll, forceOpen }) => {
  const variants = product.variants || [];
  const chosen = variants.filter(v => selected.has(v.variant_id)).length;
  const [open, setOpen] = useState(chosen > 0);
  const isOpen = forceOpen || open;

  if (variants.length === 0) {
    return (
      <div className="px-2 py-1.5 text-xs text-gray-400 dark:text-gray-500">
        {product.name} — no SKUs synced
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-gray-50 dark:hover:bg-gray-700/40">
        <button
          type="button"
          onClick={() => setOpen(o => !o)}
          className="flex items-center gap-1.5 flex-1 min-w-0 text-left"
        >
          <ChevronRight
            className={`w-3.5 h-3.5 flex-shrink-0 text-gray-400 transition-transform ${
              isOpen ? 'rotate-90' : ''
            }`}
          />
          <span className="truncate text-sm text-gray-800 dark:text-gray-200">
            {product.name}
          </span>
        </button>
        <span className={`text-xs flex-shrink-0 ${
          chosen ? 'text-brandColor font-medium' : 'text-gray-400'
        }`}>
          {chosen}/{variants.length}
        </span>
        <button
          type="button"
          onClick={() => onToggleAll(variants.map(v => v.variant_id), chosen !== variants.length)}
          className="text-xs text-gray-500 hover:text-brandColor flex-shrink-0"
        >
          {chosen === variants.length ? 'None' : 'All'}
        </button>
      </div>

      {isOpen && (
        <div className="pl-7 pb-1 grid grid-cols-1 sm:grid-cols-2 gap-1">
          {variants.map(v => (
            <label
              key={v.variant_id}
              className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300 cursor-pointer py-0.5"
            >
              <input
                type="checkbox"
                checked={selected.has(v.variant_id)}
                onChange={() => onToggle(v.variant_id)}
                className="h-3.5 w-3.5 rounded text-brandColor focus:ring-brandColor flex-shrink-0"
              />
              <span className="truncate">{variantLabel(v)}</span>
              {v.price != null && (
                <span className="text-xs text-gray-400 flex-shrink-0">₹{v.price}</span>
              )}
            </label>
          ))}
        </div>
      )}
    </div>
  );
};

/**
 * Scope chooser, collapsed to one row.
 *
 * The five scopes were radio cards with their hints always on screen — around a third of
 * the modal spent on a choice made once, leaving the SKU tree underneath it short enough
 * to need scrolling for almost every pick. Each hint now shows on its own option while
 * the menu is open, which is when it is actually being read.
 */
const ScopeDropdown: React.FC<{
  scope: AudienceScope;
  onChange: (s: AudienceScope) => void;
}> = ({ scope, onChange }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const current = AUDIENCE_SCOPES.find(s => s.value === scope) || AUDIENCE_SCOPES[0];

  // The modal body scrolls, and the audience section sits at the bottom of it, so an
  // open menu can hang below the fold. Pull it into view rather than leaving someone
  // to wonder why clicking did nothing visible.
  useEffect(() => {
    if (open) panelRef.current?.scrollIntoView({ block: 'nearest' });
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      // The picker sits inside a modal that also closes on Escape — stop the event so
      // the first press shuts the menu rather than the whole modal.
      if (e.key === 'Escape') { e.stopPropagation(); setOpen(false); }
    };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey, true);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey, true);
    };
  }, [open]);

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        aria-haspopup="listbox"
        aria-expanded={open}
        className="w-full flex items-center gap-2 px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-900 dark:text-white hover:bg-gray-50 dark:hover:bg-gray-800 focus:ring-2 focus:ring-brandColor focus:border-transparent"
      >
        <span className="text-brandColor flex-shrink-0">{current.icon}</span>
        <span className="flex-1 text-left truncate text-gray-900 dark:text-white">
          {current.label}
        </span>
        <ChevronDown className={`w-4 h-4 flex-shrink-0 text-gray-400 transition-transform ${
          open ? 'rotate-180' : ''
        }`} />
      </button>

      {open && (
        <div
          role="listbox"
          ref={panelRef}
          className="absolute z-20 mt-1 w-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg overflow-hidden"
        >
          {AUDIENCE_SCOPES.map(s => (
            <button
              key={s.value}
              type="button"
              role="option"
              aria-selected={scope === s.value}
              onClick={() => { onChange(s.value); setOpen(false); }}
              className={`w-full flex items-start gap-2.5 px-3 py-2 text-left transition-colors ${
                scope === s.value
                  ? 'bg-green-50 dark:bg-green-900/20'
                  : 'hover:bg-gray-50 dark:hover:bg-gray-700/40'
              }`}
            >
              <span className="text-brandColor flex-shrink-0 mt-0.5">{s.icon}</span>
              <span className="flex-1 min-w-0">
                <span className="block text-sm font-medium text-gray-900 dark:text-white">
                  {s.label}
                </span>
                <span className="block text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                  {s.hint}
                </span>
              </span>
              {scope === s.value && (
                <Check className="w-4 h-4 flex-shrink-0 text-brandColor mt-0.5" />
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const AudiencePicker: React.FC<Props> = ({
  tree, scope, onScopeChange, productId, onProductIdChange,
  productIds, onProductIdsChange, categoryIds, onCategoryIdsChange,
  variantIds, onVariantIdsChange,
}) => {
  const [skuSearch, setSkuSearch] = useState('');

  const covered = useMemo(
    () => resolveCoveredProductIds(scope, tree, {
      productId, productIds, categoryIds, variantIds,
    }).length,
    [scope, tree, productId, productIds, categoryIds, variantIds]
  );

  // One-line read-out of the current choice: what is selected, and what it covers.
  const selectionNote = useMemo(() => {
    const products = `${covered} product${covered === 1 ? '' : 's'}`;
    switch (scope) {
      case 'all':
        return `Every product in the catalogue — ${products}.`;
      case 'variants':
        return variantIds.size
          ? `${variantIds.size} SKU${variantIds.size === 1 ? '' : 's'} selected, across ${products}.`
          : 'No SKUs selected yet — pick the packs below.';
      case 'products':
        return productIds.size
          ? `${productIds.size} product${productIds.size === 1 ? '' : 's'} selected — every pack size of each.`
          : 'No products selected yet — pick them below.';
      case 'category': {
        if (!categoryIds.size) return 'No categories selected yet — pick them below.';
        const names = tree.filter(c => categoryIds.has(c.cat_id)).map(c => c.cat);
        const label = names.length === 1 ? names[0] : `${names.length} categories`;
        return `${label} — ${products}.`;
      }
      case 'product':
      default: {
        const p = tree.flatMap(c => c.products).find(x => x.product_id === productId);
        return p
          ? `${p.name} — every pack size of it.`
          : 'No product chosen yet — pick one below.';
      }
    }
  }, [scope, covered, variantIds, productIds, categoryIds, productId, tree]);

  const toggle = (set: Set<number>, id: number, apply: (s: Set<number>) => void) => {
    const next = new Set(set);
    next.has(id) ? next.delete(id) : next.add(id);
    apply(next);
  };

  const toggleMany = (ids: number[], select: boolean) => {
    const next = new Set(variantIds);
    ids.forEach(id => (select ? next.add(id) : next.delete(id)));
    onVariantIdsChange(next);
  };

  // Searching over 76 products × ~12 packs is the difference between usable and not.
  // A match on the product name keeps all its packs; a match on a SKU narrows to it.
  const filteredTree = useMemo(() => {
    const q = skuSearch.trim().toLowerCase();
    if (!q) return tree;
    return tree
      .map(cat => ({
        ...cat,
        products: cat.products
          .map(p => {
            if (p.name.toLowerCase().includes(q)) return p;
            const variants = (p.variants || []).filter(v =>
              (v.sku || '').toLowerCase().includes(q) ||
              (v.name || '').toLowerCase().includes(q)
            );
            return variants.length ? { ...p, variants } : null;
          })
          .filter(Boolean) as TreeProduct[],
      }))
      .filter(cat => cat.products.length > 0);
  }, [tree, skuSearch]);

  return (
    <div className="space-y-3">
      <ScopeDropdown scope={scope} onChange={onScopeChange} />

      {/* What the current choice adds up to, in one line. Replaces the always-open hint
          under each scope — and says whether anything is actually selected yet, which
          the hints never did. */}
      <p className="text-xs text-gray-500 dark:text-gray-400 px-0.5">
        {selectionNote}
      </p>

      {scope === 'variants' && (
        <div className="space-y-2">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              value={skuSearch}
              onChange={(e) => setSkuSearch(e.target.value)}
              placeholder="Search a product or SKU — e.g. HAW180, 60 Capsules"
              className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-900 dark:text-white focus:ring-2 focus:ring-brandColor focus:border-transparent"
            />
          </div>

          <div className="border border-gray-200 dark:border-gray-700 rounded-lg divide-y divide-gray-100 dark:divide-gray-700 max-h-96 overflow-y-auto">
            {filteredTree.length === 0 && (
              <div className="p-4 text-center text-xs text-gray-500 dark:text-gray-400">
                Nothing matches “{skuSearch}”.
              </div>
            )}
            {filteredTree.map(cat => (
              <div key={cat.cat_id} className="p-2">
                <div className="flex items-center gap-2 mb-1 px-1">
                  <span className="w-2 h-2 rounded-full"
                        style={{ background: cat.colour || '#9ca3af' }} />
                  <span className="text-xs font-semibold text-gray-600 dark:text-gray-300">
                    {cat.cat}
                  </span>
                </div>
                {cat.products.map(p => (
                  <VariantProductRow
                    key={p.product_id}
                    product={p}
                    selected={variantIds}
                    onToggle={(id) => toggle(variantIds, id, onVariantIdsChange)}
                    onToggleAll={toggleMany}
                    forceOpen={skuSearch.trim().length > 0}
                  />
                ))}
              </div>
            ))}
          </div>

          {variantIds.size > 0 && (
            <button
              type="button"
              onClick={() => onVariantIdsChange(new Set())}
              className="text-xs text-gray-500 hover:text-brandColor"
            >
              Clear all {variantIds.size} selected
            </button>
          )}
        </div>
      )}

      {scope === 'product' && (
        <select
          value={productId ?? ''}
          onChange={(e) => onProductIdChange(e.target.value ? Number(e.target.value) : null)}
          className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-900 dark:text-white focus:ring-2 focus:ring-brandColor focus:border-transparent"
        >
          <option value="">Choose a product…</option>
          {tree.map(cat => (
            <optgroup key={cat.cat_id} label={cat.cat}>
              {cat.products.map(p => (
                <option key={p.product_id} value={p.product_id}>{p.name}</option>
              ))}
            </optgroup>
          ))}
        </select>
      )}

      {scope === 'products' && (
        <div className="border border-gray-200 dark:border-gray-700 rounded-lg divide-y divide-gray-100 dark:divide-gray-700 max-h-80 overflow-y-auto">
          {tree.map(cat => (
            <div key={cat.cat_id} className="p-3">
              <div className="flex items-center gap-2 mb-2">
                <span className="w-2 h-2 rounded-full"
                      style={{ background: cat.colour || '#9ca3af' }} />
                <span className="text-xs font-semibold text-gray-600 dark:text-gray-300">
                  {cat.cat}
                </span>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-1">
                {cat.products.map(p => (
                  <label key={p.product_id}
                         className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={productIds.has(p.product_id)}
                      onChange={() => toggle(productIds, p.product_id, onProductIdsChange)}
                      className="h-3.5 w-3.5 rounded text-brandColor focus:ring-brandColor"
                    />
                    <span className="truncate">{p.name}</span>
                  </label>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {scope === 'category' && (
        <div className="border border-gray-200 dark:border-gray-700 rounded-lg divide-y divide-gray-100 dark:divide-gray-700 max-h-80 overflow-y-auto">
          {tree.map(cat => (
            <label key={cat.cat_id}
                   className="flex items-center gap-3 p-3 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/40">
              <input
                type="checkbox"
                checked={categoryIds.has(cat.cat_id)}
                onChange={() => toggle(categoryIds, cat.cat_id, onCategoryIdsChange)}
                className="h-4 w-4 rounded text-brandColor focus:ring-brandColor"
              />
              <span className="w-2.5 h-2.5 rounded-full"
                    style={{ background: cat.colour || '#9ca3af' }} />
              <span className="flex-1 text-sm text-gray-800 dark:text-gray-200">{cat.cat}</span>
              <span className="text-xs text-gray-400">
                {cat.products.length} product{cat.products.length === 1 ? '' : 's'}
              </span>
            </label>
          ))}
        </div>
      )}

      {/* Overlap is legal and resolved by specificity, so say so rather than letting
          someone assume two journeys will both fire. The coverage counts moved up to
          the note under the dropdown, leaving only the rule to state here. */}
      <div className="flex items-start gap-2 px-0.5 text-xs text-gray-500 dark:text-gray-400">
        <Info className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
        <span>
          An order starts only <strong>one</strong> journey — the most specific match
          wins: SKU → product → category → every product.
        </span>
      </div>
    </div>
  );
};

export default AudiencePicker;
